<template>
  <div class="my-apps-page container py-5">
    <header class="head">
      <h2 v-tx="'MyApplicationsTitle'">{{ t('title') }}</h2>
      <p class="muted" v-tx="'MyApplicationsSubtitle'">{{ t('subtitle') }}</p>
    </header>

    <div class="stats-bar">
      <div class="stat" v-for="s in stats" :key="s.code">
        <div class="stat-top">
          <span class="dot" :style="{ background: s.dot }"></span>
          <span class="stat-label">{{ statusLabel(s.code) }}</span>
        </div>
        <div class="stat-num" :style="{ color: s.num }">{{ s.count }}</div>
      </div>
    </div>

    <div v-if="loading" class="status" v-tx="'MyApplicationsLoading'">{{ t('loading') }}</div>
    <div v-else-if="!items.length" class="status" v-tx="'MyApplicationsEmpty'">{{ t('empty') }}</div>

    <div v-else class="apps-table-wrap">
      <table class="apps-table">
        <thead>
          <tr>
            <th v-tx="'MyApplicationsColBewerber'">{{ t('colBewerber') }}</th>
            <th v-tx="'MyApplicationsColFach'">{{ t('colFach') }}</th>
            <th v-tx="'MyApplicationsColErfahrung'">{{ t('colErfahrung') }}</th>
            <th v-tx="'MyApplicationsColSprache'">{{ t('colSprache') }}</th>
            <th v-tx="'MyApplicationsColStatus'">{{ t('colStatus') }}</th>
            <th class="ta-right" v-tx="'MyApplicationsColAktion'">{{ t('colAktion') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in items" :key="a.id">
            <td>
              <div class="bewerber">
                <span class="avatar" :style="{ background: avatarColor(a) }">{{ initials(a) }}</span>
                <span class="name">{{ a.nurseFullName }}</span>
              </div>
            </td>
            <td>{{ (a.specialties || []).join(', ') || '—' }}</td>
            <td v-tx="'MyApplicationsYearsSuffix'">{{ a.experienceYears }} {{ t('yearsSuffix') }}</td>
            <td>{{ (a.languages || []).join(', ') || '—' }}</td>
            <td>
              <span class="status-pill"
                    :style="{ color: statusMeta(a.statusCode).color, background: statusMeta(a.statusCode).color + '1F' }">
                {{ statusLabel(a.statusCode) }}
              </span>
            </td>
            <td class="ta-right">
              <button type="button" class="ansehen" @click="goNurse(a.nurseProfileId)" v-tx="'MyApplicationsView'">{{ t('view') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const STATUS_DEFS = [
  { code: "Neu", dot: "#9EA6B6", num: "#151924" },
  { code: "InPruefung", dot: "#294C8E", num: "#294C8E" },
  { code: "Vorstellungsgespraech", dot: "#894FE5", num: "#894FE5" },
  { code: "Dokumentation", dot: "#F39C12", num: "#F39C12" },
  { code: "Visaprozess", dot: "#0FA5C6", num: "#0FA5C6" },
  { code: "Eingestellt", dot: "#10B881", num: "#10B881" },
];

const AVATAR_PALETTE = ["#132B59", "#894FE5", "#0FA5C6", "#10B881", "#F39C12", "#EF4444"];

// Status mərhələlərinin tərcüməsi
const STATUS_T = {
  Neu: { az: "Yeni", de: "Neu", en: "New", ru: "Новый", tr: "Yeni" },
  InPruefung: { az: "Yoxlanışda", de: "In Prüfung", en: "In review", ru: "На проверке", tr: "İncelemede" },
  Vorstellungsgespraech: { az: "Müsahibə", de: "Vorstellungsgespräch", en: "Interview", ru: "Собеседование", tr: "Mülakat" },
  Dokumentation: { az: "Sənədləşmə", de: "Dokumentation", en: "Documentation", ru: "Документы", tr: "Belgeleme" },
  Visaprozess: { az: "Viza prosesi", de: "Visaprozess", en: "Visa process", ru: "Виза", tr: "Vize süreci" },
  Eingestellt: { az: "İşə götürülüb", de: "Eingestellt", en: "Hired", ru: "Принят", tr: "İşe alındı" },
};

const T = {
  az: { title: "Müraciətlərim", subtitle: "Bu siyahıda namizədlərə göndərdiyiniz müraciətlər toplanır.", loading: "Yüklənir…", empty: "Hələ heç bir müraciətiniz yoxdur.", colBewerber: "Namizəd", colFach: "İxtisas", colErfahrung: "Təcrübə", colSprache: "Dil", colStatus: "Status", colAktion: "Əməliyyat", yearsSuffix: "il", view: "Bax" },
  de: { title: "Meine Bewerbungen", subtitle: "Hier werden die an Kandidaten gesendeten Bewerbungen gesammelt.", loading: "Wird geladen…", empty: "Noch keine Bewerbungen.", colBewerber: "Bewerber", colFach: "Fachrichtung", colErfahrung: "Erfahrung", colSprache: "Sprache", colStatus: "Status", colAktion: "Aktion", yearsSuffix: "Jahre", view: "Ansehen" },
  en: { title: "My applications", subtitle: "Applications you sent to candidates are collected here.", loading: "Loading…", empty: "No applications yet.", colBewerber: "Candidate", colFach: "Specialty", colErfahrung: "Experience", colSprache: "Language", colStatus: "Status", colAktion: "Action", yearsSuffix: "yrs", view: "View" },
  ru: { title: "Мои заявки", subtitle: "Здесь собраны заявки, отправленные кандидатам.", loading: "Загрузка…", empty: "Заявок пока нет.", colBewerber: "Кандидат", colFach: "Специальность", colErfahrung: "Опыт", colSprache: "Язык", colStatus: "Статус", colAktion: "Действие", yearsSuffix: "лет", view: "Открыть" },
  tr: { title: "Başvurularım", subtitle: "Adaylara gönderdiğiniz başvurular burada toplanır.", loading: "Yükleniyor…", empty: "Henüz başvuru yok.", colBewerber: "Aday", colFach: "Uzmanlık", colErfahrung: "Deneyim", colSprache: "Dil", colStatus: "Durum", colAktion: "İşlem", yearsSuffix: "yıl", view: "Gör" },
};

export default {
  name: "MyApplicationsPage",
  data() {
    return { items: [], statusCounts: {}, loading: true };
  },
  computed: {
    lang() { return this.$store.state.lang || "az"; },
    stats() {
      return STATUS_DEFS.map((d) => ({ ...d, count: this.statusCounts[d.code] || 0 }));
    },
  },
  mounted() { this.load(); },
  methods: {
    t(key) {
      const sKey = "MyApplications" + key.charAt(0).toUpperCase() + key.slice(1);
      const ov = this.$store.state.statics && this.$store.state.statics[sKey];
      if (ov && String(ov).trim()) return ov;
      const d = T[this.lang] || T.az;
      return d[key] != null ? d[key] : (T.az[key] || key);
    },
    statusLabel(code) { const m = STATUS_T[code]; return m ? (m[this.lang] || m.az) : (code || "—"); },
    async load() {
      this.loading = true;
      try {
        const res = await axios.get(`/clinic/applications`);
        const data = res.data?.data;
        // Yeni format: { items, statusCounts }. Köhnə format: birbaşa array.
        this.items = Array.isArray(data) ? data : (data?.items || []);
        this.statusCounts = (data && data.statusCounts) || {};
      } finally {
        this.loading = false;
      }
    },
    goNurse(id) { this.$router.push({ name: "candidate-detail", params: { id } }); },
    statusMeta(code) {
      const d = STATUS_DEFS.find((x) => x.code === code);
      return { color: d ? d.dot : "#9EA6B6" };
    },
    avatarColor(a) {
      const idx = (Number(a.nurseProfileId) || 0) % AVATAR_PALETTE.length;
      return AVATAR_PALETTE[idx];
    },
    initials(a) {
      // yalnız hərflə başlayan hissələr (test adlarındakı "--" kimi simvolları at)
      const parts = (a.nurseFullName || "").trim().split(/\s+/)
        .filter((p) => /^[\p{L}]/u.test(p));
      const f = (parts[0] || "").charAt(0).toUpperCase();
      const s = (parts[1] || "").charAt(0).toUpperCase();
      return (f + s) || "?";
    },
    formatDate(d) {
      if (!d) return "";
      try { return new Date(d).toLocaleString('az-AZ', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
      catch { return d; }
    },
  },
};
</script>

<style lang="scss" scoped>
.my-apps-page { min-height: 60vh; max-width: 1120px;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
}
.head { margin-bottom: 16px;
  h2 { color: #132B59; margin: 0 0 8px; font-size: 22px; font-weight: 700; line-height: 1; }
  .muted { color: #666F82; margin: 0; font-size: 14px; font-weight: 400; line-height: 1; }
}

.stats-bar {
  width: 100%; max-width: 1080px; min-height: 60px;
  border: 1px solid #E0E3EA; border-radius: 12px; background: #fff;
  display: flex; align-items: stretch; padding: 0 8px; margin-bottom: 20px;

  .stat { flex: 1; min-width: 0; padding: 10px 14px; display: flex; flex-direction: column; justify-content: center; gap: 8px;
    &:not(:first-child) { border-left: 1px solid #EBEDF1; }
    .stat-top { display: flex; align-items: center; gap: 7px;
      .dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
      .stat-label { font-size: 11px; font-weight: 500; color: #666F82; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    }
    .stat-num { font-size: 18px; font-weight: 700; line-height: 1; color: #151924; }
  }

  @media (max-width: 767px) {
    flex-wrap: wrap; padding: 8px;
    .stat { flex: 0 0 33.333%;
      &:nth-child(3n+1) { border-left: 0; }
    }
  }
}

.status { background: #fff; padding: 1.5rem; border-radius: 8px; text-align: center; color: #666; }

/* === Ümumi cədvəl === */
.apps-table-wrap {
  width: 100%; max-width: 1080px;
  border: 1px solid #E0E3EA; border-radius: 12px;
  overflow: hidden; background: #fff;
}

.apps-table {
  width: 100%; border-collapse: collapse; table-layout: fixed;

  /* sütun enləri (cəmi 100%) */
  th:nth-child(1), td:nth-child(1) { width: 24%; }
  th:nth-child(2), td:nth-child(2) { width: 17%; }
  th:nth-child(3), td:nth-child(3) { width: 12%; }
  th:nth-child(4), td:nth-child(4) { width: 18%; }
  th:nth-child(5), td:nth-child(5) { width: 17%; }
  th:nth-child(6), td:nth-child(6) { width: 12%; }

  thead tr { height: 40px; background: #F5F6FA; }
  thead th {
    text-align: left; vertical-align: middle; padding: 0 16px;
    font-size: 10px; font-weight: 600; color: #9EA6B6;
    line-height: 1; white-space: nowrap;
    &.ta-right { text-align: right; }
  }

  /* --- sətir/xana (ilkin — token gələndə dəqiqləşəcək) --- */
  tbody td {
    padding: 12px 16px; font-size: 13px; color: #151924; vertical-align: middle;
    border-top: 1px solid #EBEDF1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    &.ta-right { text-align: right; }
  }

  .bewerber { display: flex; align-items: center; gap: 10px;
    .avatar { width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; color: #fff; font-size: 11px; font-weight: 600; display: inline-flex; align-items: center; justify-content: center; text-transform: uppercase; }
    .name { flex: 1; min-width: 0; font-weight: 600; color: #132B59; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }

  .status-pill { display: inline-flex; align-items: center; height: 22px; padding: 0 10px; border-radius: 11px; font-size: 11px; font-weight: 600; }

  .ansehen { height: 32px; padding: 0 14px; border: 1px solid #E0E3EA; border-radius: 8px; background: #fff; color: #666F82; font: inherit; font-size: 12px; cursor: pointer;
    &:hover { background: #f7f9fc; }
  }
}
</style>
